import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import type { SupportTicketStatus } from '@/services/supportService';

type Variant = 'default' | 'secondary' | 'warning' | 'success' | 'destructive' | 'outline';

/**
 * Support-ticket-status → badge-variant mapping (F161 FR4). Constrained to
 * the Resend-style monochrome-with-tinted-status palette (docs/07 §3.2 — no
 * ``--color-info``):
 *
 *   open        → default (darkest; fresh, unhandled ticket)
 *   in_analysis → warning (support is actively working it)
 *   closed      → success (closed-positive)
 *
 * Read-only here — status transitions are F162's (admin side) concern.
 */
const STATUS_VARIANT: Record<SupportTicketStatus, Variant> = {
  open: 'default',
  in_analysis: 'warning',
  closed: 'success',
};

interface SupportTicketStatusBadgeProps {
  status: SupportTicketStatus;
  className?: string;
  'data-testid'?: string;
}

export function SupportTicketStatusBadge({
  status,
  className,
  'data-testid': testId,
}: SupportTicketStatusBadgeProps) {
  const { t } = useTranslation();
  const variant = STATUS_VARIANT[status];
  return (
    <Badge
      variant={variant}
      className={className}
      data-testid={testId}
      data-variant={variant}
      data-status={status}
    >
      <span className="mr-1 h-1.5 w-1.5 rounded-full bg-current opacity-70" aria-hidden />
      {t(`support.status.${status}`)}
    </Badge>
  );
}
